import { useCallback, useState } from 'react'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { useAppDispatch } from 'common/hooks/redux'

import {
  TEAM_REQUESTS_TAG,
  teamChatApi,
  useUpdateTeamRequestMutation,
} from 'features/TeamChat/api/teamChat.api'
import { teamErrorText } from 'features/TeamChat/api/teamChat.transform'
import type { TeamRequestAction } from 'features/TeamChat/api/teamChat.types'
import { TEAM_COPY } from 'features/TeamChat/teamChat.constants'

const CONFLICT = 409

// Each card is busy until its own request settles.
export const useRequestActions = () => {
  const [busyIds, setBusyIds] = useState<ReadonlySet<number>>(new Set())
  const [updateRequest] = useUpdateTeamRequestMutation()
  const { setNotification } = useNotification()
  const dispatch = useAppDispatch()

  const run = useCallback(
    (id: number, action: TeamRequestAction) => {
      if (busyIds.has(id)) return
      setBusyIds((ids) => new Set([...ids, id]))
      updateRequest({ id, action })
        .unwrap()
        .catch((error: { status?: unknown }) => {
          // Only a lost claim race means someone else took it; other conflicts explain themselves.
          const takenByOther = action === 'claim' && error?.status === CONFLICT
          const description = takenByOther
            ? TEAM_COPY.ALREADY_TAKEN
            : teamErrorText(error, TEAM_COPY.SEND_FAILED)
          setNotification({
            type: ALERT_CONSTANTS.ERROR,
            title: TEAM_COPY.ERROR_TITLE,
            description,
          })
          dispatch(teamChatApi.util.invalidateTags([TEAM_REQUESTS_TAG]))
        })
        .finally(() => setBusyIds((ids) => new Set([...ids].filter((x) => x !== id))))
    },
    [busyIds, updateRequest, setNotification, dispatch],
  )

  return { busyIds, run }
}
